import type { FC } from 'react';
import Tooltip from '@components/Tooltip';
import clsx from 'clsx';
import { CircleCheck, Radar } from 'lucide-react';
import pluralize from 'pluralize';

interface GuidanceCountBadgeProps {
  /** Canonical suggestions from the last completed guidance scan; undefined = never scanned. */
  findingsCount?: number;
  /** Of those, resolved or dismissed. */
  reviewedCount?: number;
}

export type GuidanceSummary = {
  /** Suggestions still to review. */
  open: number;
  /** Number shown in the cell: open suggestions while any remain, else the total reviewed. */
  shown: number;
  tooltip: string;
};

/** The three states of the contracts-list Guidance cell (product screens, Sept 2026). */
export const getGuidanceSummary = (findingsCount: number, reviewedCount = 0): GuidanceSummary => {
  const open = Math.max(findingsCount - reviewedCount, 0);
  if (findingsCount === 0) {
    return { open, shown: 0, tooltip: 'No suggestions found' };
  }
  if (open > 0) {
    return {
      open,
      shown: open,
      tooltip: `${open} of ${findingsCount} ${pluralize('suggestion', findingsCount)} to review`,
    };
  }
  return { open, shown: findingsCount, tooltip: `${findingsCount} ${pluralize('suggestion', findingsCount)} reviewed` };
};

/**
 * Contracts-list Guidance cell, per the Sept 2026 product screens. Blank until
 * a scan has completed; then a small icon followed by a plain number — a radar
 * in a violet disc with the open count while suggestions remain, and a grey
 * check with the reviewed total once they are all closed (or none were found).
 * No pill: the number reads as table text.
 */
export const GuidanceCountBadge: FC<GuidanceCountBadgeProps> = ({ findingsCount, reviewedCount }) => {
  if (findingsCount === undefined) {
    return null;
  }

  const { open, shown, tooltip } = getGuidanceSummary(findingsCount, reviewedCount);
  const hasOpen = open > 0;

  return (
    <Tooltip content={tooltip}>
      <div
        className={clsx(
          'inline-flex items-center gap-1 text-sm leading-none',
          hasOpen ? 'text-violet-600' : 'text-gray-500',
        )}
        data-testid="guidance-count-badge"
      >
        {hasOpen ? (
          <span className="inline-flex size-4 items-center justify-center rounded-full bg-violet-100">
            <Radar aria-hidden className="size-2.5 text-violet-600" />
          </span>
        ) : (
          <CircleCheck aria-hidden className="size-4" />
        )}
        {shown}
      </div>
    </Tooltip>
  );
};
